import { FunctionComponent, useEffect, useRef, useState } from "react"
import { Button, CircularProgress, Typography } from "@mui/material"
import logger from "@utils/logger"
import { GraphDto } from "@custom-types/graphs"
import {
  GraphValidationResultDto,
  ValidationNodeDto,
  useLazyGetValidateGraphLambdaQuery,
  useValidateGraphLambdaMutation,
} from "@store/services/documentService"
import { GraphDocument } from "@features/builder/types"
import { formatFormDate } from "@features/builder/UpdateMetadata/helpers"
import { SamplingValidationResults } from "@features/builder/GraphValidation/SamplingValidationResults"
import Dialog from "@components/Dialog"
import DialogContent from "@mui/material/DialogContent"
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query/react"
import { formatValidationServerError } from "@features/builder/GraphValidation/validationUitls"
import { errorsDrawerOpenAtom } from "@features/builder/ErrorsDrawer/errorsDrawerOpenAtom"
import { presamplingValidationAtom } from "@features/builder/graph/nodes/validation/presamplingValidationAtom"
import { raisePresamplingValidationResults } from "@features/builder/graph/validation/errors"
import { useAtom, useSetAtom } from "jotai"

export interface GraphValidationData {
  doc: GraphDocument
  startDate: string
  endDate: string
  scenarios: string[]
  sha256: string
  nodes: { [key: string]: ValidationNodeDto }
}

const VALIDATION_FETCH_TIMEOUT = 5000

enum ComponentState {
  ERROR = "ERROR",
  WARNING = "WARNING",
  OK = "OK",
  UNKNOWN = "UNKNOWN",
}

const getComponentState = (msg?: GraphValidationResultDto) => {
  if (!msg) return ComponentState.UNKNOWN
  if (msg.message && msg.message.length > 0) return ComponentState.ERROR
  if (msg.warnings && msg.warnings.length > 0) return ComponentState.WARNING
  return ComponentState.OK
}

export const GraphValidationDialog: FunctionComponent<{
  open: boolean
  onClose: () => void
  graph: GraphDto
  data: GraphValidationData
  onValidationSuccess: (doc: GraphDocument, sanityHash: string) => void
}> = ({ open, onClose, graph, data, onValidationSuccess }) => {
  const [triggerValidateLambda, { isLoading: isValidating }] = useValidateGraphLambdaMutation()
  const [fetchValidationGraphResults, { isLoading: isFetchingValidation }] =
    useLazyGetValidateGraphLambdaQuery()
  const [validationResult, setValidationResult] = useAtom(presamplingValidationAtom)
  const setErrorsDrawerOpen = useSetAtom(errorsDrawerOpenAtom)
  const [sanityHash, setSanityHash] = useState("")
  const isComponentMounted = useRef(false)
  const isLoading = isValidating || isFetchingValidation

  const setValidationErrors = (result: GraphValidationResultDto) => {
    setValidationResult(result)

    if (result.message?.length) {
      setErrorsDrawerOpen(true)
      raisePresamplingValidationResults()
    }
  }

  useEffect(() => {
    // do not proceed any further if dialog is not open
    if (!open) return

    // do not call useEffect twice
    if (isComponentMounted.current) return
    isComponentMounted.current = true

    setValidationResult(undefined)
    setSanityHash("")
    triggerValidateLambda({
      key: graph.key,
      start_date: formatFormDate(data.startDate),
      end_date: formatFormDate(data.endDate),
      scenarios: data.scenarios ?? [],
    })
      .then((response) => {
        if ("error" in response) {
          const { error } = response
          setValidationErrors({
            message: [formatValidationServerError(error as FetchBaseQueryError)],
          })
          return
        }

        const { data: triggerResult } = response
        if (!triggerResult.response.message_id) {
          setValidationErrors({ message: ["Server Error Trigger Validation missing message_id"] })
          return
        }

        // TODO ms - sometimes hash calculated on FE is different than hash calculated on BE - it's only after editing certain way on FE
        // can't find which edition especially breaks hash but we probably need to fix it at some point
        const { message_id } = triggerResult.response
        logger.debug(`message_id ${message_id} client sha256 ${data.sha256}`)
        fetchValidationUntilStatus(message_id)
        setSanityHash(message_id)
      })
      .finally(() => {
        isComponentMounted.current = false
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, open])

  const fetchValidationUntilStatus = (messageId: string) => {
    fetchValidationGraphResults(messageId).then(({ data: fetchResult, error }) => {
      if (error) {
        setValidationErrors({
          message: [formatValidationServerError(error as FetchBaseQueryError)],
        })
        return
      }

      switch (fetchResult?.status) {
        case "ERROR":
        case "SUCCESS":
        case "TIMEOUT":
          if (getComponentState(fetchResult) === ComponentState.OK) {
            onValidationSuccess(data.doc, messageId)
            setValidationResult(undefined)
          } else {
            setValidationErrors(fetchResult)
          }
          break
        default:
          logger.debug("Status", fetchResult)
          setTimeout(() => fetchValidationUntilStatus(messageId), VALIDATION_FETCH_TIMEOUT)
      }
    })
  }

  const handleClick = () => {
    if (getComponentState(validationResult) === ComponentState.WARNING) {
      onValidationSuccess(data.doc, sanityHash)
      return
    }
    onClose()
  }

  const status = getComponentState(validationResult)

  return (
    <Dialog open={open} onClose={onClose} title={`Validate ${graph.name}`} maxWidth={false}>
      <DialogContent sx={{ minWidth: 800, maxWidth: "100%", borderRadius: "30px", minHeight: 600 }}>
        <div style={{ padding: 10 }}>
          {(isLoading || !validationResult) && <CircularProgress />}
          {!isLoading && validationResult && <GraphValidationResult result={validationResult} />}
          <div style={{ padding: 10 }}>
            {status !== ComponentState.OK && (
              <Button
                variant="contained"
                style={{
                  backgroundColor: "#D6002A",
                  padding: "10px",
                }}
                onClick={handleClick}
              >
                <b>
                  {status === ComponentState.UNKNOWN && "Cancel"}
                  {status === ComponentState.ERROR && "Close"}
                  {status === ComponentState.WARNING && "Continue Anyway"}
                </b>
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

const GraphValidationResult: FunctionComponent<{ result: GraphValidationResultDto }> = ({
  result,
}) => {
  const { message: errors, warnings } = result
  const isError = errors && errors.length > 0
  const isWarning = warnings && warnings.length > 0 && !isError
  const isOk = !isError && !isWarning

  return (
    <div style={{ width: "100%" }}>
      <Typography variant="h5">
        Graph Validation Status: {isOk && <span style={{ color: "limegreen" }}>OK</span>}
        {isError && <span style={{ color: "#D6002A" }}>Error</span>}
        {isWarning && <span style={{ color: "#ecc649" }}>Warning</span>}
      </Typography>
      <SamplingValidationResults result={result} />
    </div>
  )
}
